import { type ChangeEvent, type ReactNode } from 'react'
import styled from 'styled-components'
import { Copy, Tooltip, formatAmountForDisplay, t } from '@stokr/components-library'
import type { CopyKey } from 'locales/copy-keys.gen'
import { useInvestPanel } from 'hooks/sto/useInvestPanel'
import { SectionSkeleton } from 'components/sto/Section'
import { OptionMenu } from 'components/sto/OptionMenu'
import { Notice } from './Notice'
import { stoColor, stoSpace, stoType } from 'constants/stoTheme'
import {
  AmountBox,
  AmountInput,
  CurrencyPill,
  FieldGroup,
  FieldHeaderRow,
  FieldHint,
  FieldLabel,
  LockedTrigger,
  PrimaryAction,
  type FieldTone,
} from './KeyActions.styles'

/** The Amount and Securities fields' shared frame (Figma 3329:68553). */
const Fields = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const REVEAL = '300ms cubic-bezier(0.4, 0, 0.2, 1)'

/** Opens the Securities field once there is something to show: rows go 0fr → 1fr, the field fades in. */
const Reveal = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition:
    grid-template-rows ${REVEAL},
    opacity ${REVEAL};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/** The Fields gap lives here, so a closed field takes no space. */
const RevealClip = styled.div`
  min-height: 0;
  overflow: hidden;

  > * {
    padding-top: ${stoSpace[2]};
  }
`

/**
 * The minimum and the notes under the fields are Figma's text/tertiary (#9b9b9b) here. Lighter than the
 * theme's textTertiary, which stays darker for contrast elsewhere (see stoTheme).
 */
const Caption = styled.p`
  ${stoType.bodySm};
  margin: 0;
  color: ${stoColor.neutral500};
`

const MinimumHint = styled(FieldHint)`
  color: ${stoColor.neutral500};
`

/** The currency picker outlined at 2px, as the securities pill beside it. */
const CurrencySwitch = styled.span`
  display: inline-flex;

  &&& button {
    border-width: 2px;
  }
`

/**
 * Grouped display of the raw amount. The library groups and keeps typed decimals but
 * drops a trailing "." — kept here so a decimal can be typed. Interim: this becomes the
 * shared `AmountField` once it's promoted to the components library.
 */
const toAmountDisplay = (raw: string): string =>
  raw.endsWith('.') ? `${formatAmountForDisplay(raw.slice(0, -1))}.` : formatAmountForDisplay(raw)

interface AmountFieldProps {
  ariaLabelKey: CopyKey
  value: string
  ticker: string
  tone: FieldTone
  /** Also off while the project is loading. */
  disabled?: boolean
  onChange?: (value: string) => void
  onBlur?: () => void
  dataCy: string
  /** In place of the ticker pill: a currency picker. */
  currencyMenu?: ReactNode
}

/** One amount box: the typed field, or the one that follows it (read-only). */
const AmountField = ({
  ariaLabelKey,
  value,
  ticker,
  tone,
  disabled,
  onChange,
  onBlur,
  dataCy,
  currencyMenu,
}: AmountFieldProps) => (
  <AmountBox $tone={tone}>
    <AmountInput
      // 34px: above iOS's 16px zoom limit, so it keeps its own size (style/base).
      data-keep-font-size
      type="text"
      inputMode="decimal"
      autoComplete="off"
      placeholder="0"
      aria-label={t(ariaLabelKey)}
      value={toAmountDisplay(value)}
      disabled={disabled || tone !== 'editable'}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value.replace(/,/g, ''))}
      onBlur={onBlur}
      data-cy={dataCy}
    />
    {currencyMenu ?? (ticker && <CurrencyPill $muted={tone !== 'editable'}>{ticker}</CurrencyPill>)}
  </AmountBox>
)

/**
 * Invest tab. Whether the product is open for investment is product data, the same for every
 * visitor: closed greys out the fields and the button. Open, the fields can be typed in and the
 * button waits for full primary-market eligibility. Every product takes an amount; once one is
 * typed, a read-only Securities field shows what it buys at the sale price (a fund's latest NAV).
 */
export const InvestPanel = () => {
  const {
    amount,
    setAmount,
    securities,
    currencyCode,
    currencies,
    selectedCurrency,
    setCurrency,
    minimumLabel,
    isOpen,
    canInvest,
    lockedReasonKey,
    isResolving,
    onInvest,
    onAmountCommit,
    onLockedClick,
  } = useInvestPanel()

  return (
    <>
      <Fields>
        <FieldGroup>
          <FieldHeaderRow>
            <FieldLabel>
              <Copy id="stoPage.keyActions.invest.amount" />
            </FieldLabel>
            {minimumLabel && (
              <MinimumHint>
                <Copy id="stoPage.keyActions.invest.minimum" values={{ amount: minimumLabel }} />
              </MinimumHint>
            )}
          </FieldHeaderRow>
          <AmountField
            ariaLabelKey="stoPage.keyActions.invest.amountAria"
            value={amount}
            ticker={currencyCode}
            tone={isOpen === false ? 'closed' : 'editable'}
            disabled={isOpen === undefined}
            onChange={setAmount}
            onBlur={onAmountCommit}
            dataCy="sto-invest-amount-input"
            currencyMenu={
              currencies.length > 1 ? (
                <CurrencySwitch>
                  <OptionMenu
                    label={currencyCode}
                    menuLabel={t('stoPage.keyActions.invest.currencyMenu')}
                    options={currencies.map((item) => ({ value: item.ref, label: item.name }))}
                    selected={selectedCurrency}
                    active
                    align="end"
                    disabled={isOpen !== true}
                    dataCy="sto-invest-currency-menu"
                    onSelect={(ref) => setCurrency(ref as (typeof currencies)[number]['ref'])}
                  />
                </CurrencySwitch>
              ) : undefined
            }
          />
          {minimumLabel && amount !== '' && (
            <Caption>
              <Copy id="stoPage.keyActions.invest.minimumNote" />
            </Caption>
          )}
        </FieldGroup>

        <Reveal $open={securities.value !== ''} inert={securities.value === ''}>
          <RevealClip>
            <FieldGroup>
              <FieldLabel>
                <Copy id="stoPage.keyActions.invest.securities" />
              </FieldLabel>
              <AmountField
                ariaLabelKey="stoPage.keyActions.invest.securitiesAria"
                value={securities.value}
                ticker={securities.symbol}
                tone="readOnly"
                dataCy="sto-invest-securities-output"
              />
              <Caption>
                <Copy id="stoPage.keyActions.invest.securitiesHint" />
              </Caption>
            </FieldGroup>
          </RevealClip>
        </Reveal>
      </Fields>

      {/* aria-disabled because the library filters the `disabled` prop off the DOM node
          (it styles via pointer-events, not the native attribute) — without this a
          screen reader announces the greyed button as enabled. */}
      {isResolving ? (
        <SectionSkeleton rows={1} rowHeight={52} />
      ) : (
        <Tooltip placement="top" content={lockedReasonKey && t(lockedReasonKey)} maxWidth={280}>
          {/* Focusable only while locked, so the reason is reachable by keyboard too (as Trade). */}
          <LockedTrigger tabIndex={lockedReasonKey ? 0 : undefined} onClick={canInvest ? undefined : onLockedClick}>
            <PrimaryAction
              type="button"
              disabled={!canInvest}
              aria-disabled={!canInvest}
              data-cy="sto-invest-cta"
              onClick={onInvest}>
              <Copy id="stoPage.keyActions.invest.cta" />
            </PrimaryAction>
          </LockedTrigger>
        </Tooltip>
      )}

      {isOpen !== undefined && (
        <Notice
          variant={isOpen ? 'success' : 'warning'}
          titleKey={
            isOpen ? 'stoPage.keyActions.subscription.openTitle' : 'stoPage.keyActions.subscription.closedTitle'
          }
        />
      )}
    </>
  )
}
